import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { formatContractError } from "@/lib/error";
import { useContracts } from "@/hooks/useContracts";
import { ASSETS } from "@/lib/contracts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, SlidersHorizontal } from "lucide-react";
import { refreshAfterTx } from "@/lib/refreshAll";

interface SplitPanelProps {
  tokenId?: number | null;
  onDone?: () => void;
}

export function SplitPanel({ tokenId, onDone }: SplitPanelProps) {
  const { rounds, connected, requestConnect } = useContracts();
  const [values, setValues] = useState<number[]>([100, 0, 0]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tokenId) return;
    setIsLoading(true);
    Promise.all(ASSETS.map((_, idx) => rounds.splitOf(tokenId, idx)))
      .then((bpsArray: bigint[]) => {
        setValues(bpsArray.map((bps) => Number(bps) / 100));
      })
      .catch(() => setValues([100, 0, 0]))
      .finally(() => setIsLoading(false));
  }, [tokenId, rounds]);

  const total = values.reduce((a, b) => a + b, 0);
  const isValid = total === 100;

  const updateValue = (idx: number, raw: string) => {
    const num = Math.max(0, Math.min(100, Number(raw) || 0));
    setValues((prev) => {
      const next = [...prev];
      next[idx] = num;
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!tokenId || !isValid) return;
    setError(null);
    setIsPending(true);
    try {
      const idx = ASSETS.map((_, i) => i);
      const bps = values.map((v) => Math.round(v * 100));
      const tx = await rounds.setSplit(tokenId, idx, bps);
      await tx.wait();
      await refreshAfterTx(tokenId);
      onDone?.();
    } catch (err) {
      setError(formatContractError(err));
    } finally {
      setIsPending(false);
    }
  };

  if (!connected) {
    return (
      <Button variant="pixel" size="pixel" className="w-full" onClick={requestConnect}>
        Connect Wallet
      </Button>
    );
  }

  if (!tokenId) {
    return <p className="text-sm text-muted-foreground">Select a cat to configure rewards.</p>;
  }

  return (
    <div className="space-y-4">
      {ASSETS.map((asset, idx) => (
        <div key={asset.symbol} className="flex items-center gap-3">
          <span className="w-12 font-mono text-sm">{asset.symbol}</span>
          <Input
            type="number"
            min={0}
            max={100}
            value={isLoading ? "" : values[idx]}
            onChange={(e) => updateValue(idx, e.target.value)}
            disabled={isLoading || isPending}
            className="font-mono"
          />
          <span className="w-8 text-sm text-muted-foreground">%</span>
        </div>
      ))}

      <div className="flex items-center justify-between rounded-xl border border-border bg-background p-3">
        <span className="text-xs text-muted-foreground">Total</span>
        <span className={cn("font-mono text-sm", isValid ? "text-success" : "text-danger")}>
          {total.toFixed(0)}%
        </span>
      </div>

      <Button
        variant="pixel"
        size="pixel"
        className="w-full"
        onClick={handleSubmit}
        disabled={!isValid || isPending}
      >
        {isPending ? (
          <span className="flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" />
            Saving
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <SlidersHorizontal size={16} />
            Set Split
          </span>
        )}
      </Button>

      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  );
}
